"use client";

/**
 * useProseReveal
 * @description 긴 글의 블록(문단 · 제목 · 그림 · 인용 · 표)이 화면에 들어올 때 하나씩 떠오르게
 *   한다. `Reveal` 을 블록마다 감쌀 수 없는 자리(마크다운이 낳은 태그들)를 위한 것이다 —
 *   대상은 감싼 요소의 **직계 자식**이다. 그림은 조금 더 크게 시작해 제자리로 줄어든다.
 *
 *   움직임을 줄인 설정에서는 아무것도 하지 않는다. 애니메이션 전 상태를 CSS 로 미리 숨기지
 *   않는 것도 같은 이유다: 스크립트가 못 돌면 글이 영영 안 보인다.
 * @example
 * const ref = useProseReveal<HTMLDivElement>();
 * <div ref={ref} className="fw-prose">…</div>
 */
import { useRef, type RefObject } from "react";

import { gsap, useGSAP } from "@/shared/lib/gsap";

const RISE_PX = 24;
const DURATION_S = 0.7;
const START = "top 92%";
/** 그림은 살짝 큰 채로 시작해 제자리로 — 떠오르기만 하면 다른 블록과 같아 보인다 */
const IMAGE_SCALE = 1.04;

export function useProseReveal<E extends HTMLElement>(): RefObject<E | null> {
  const ref = useRef<E>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;
      const mm = gsap.matchMedia();
      mm.add("(prefers-reduced-motion: no-preference)", () => {
        Array.from(el.children).forEach((child) => {
          const isImage =
            child.tagName === "IMG" || Boolean(child.querySelector("img"));
          gsap.fromTo(
            child,
            { y: RISE_PX, opacity: 0, scale: isImage ? IMAGE_SCALE : 1 },
            {
              y: 0,
              opacity: 1,
              scale: 1,
              duration: DURATION_S,
              ease: "power3.out",
              clearProps: "transform,opacity",
              scrollTrigger: { trigger: child, start: START, once: true },
            },
          );
        });
      });
      return () => mm.revert();
    },
    { scope: ref },
  );

  return ref;
}
